/**
 * The things on a map. Every thing has an id, and may appear `from` a moment and leave `until` one:
 * ticks when the timeline holds it, or seconds of a scene when a scene shows it. See timeline.ts. A
 * thing on a canvas shows at the zooms its `zoom` gives. Sizes on screen are in pixels, the screen's
 * own measure, alike on every screen whatever its density. The kinds of thing:
 *
 * - canvas: a surface the others sit on, such as the world, a galaxy, a city, or a room, and the
 *   text styles words on it are set in.
 * - point: a marker at a position, a glyph framed or bare. It may pulse.
 * - line: a stroked line, such as a route, a border, or an arrow.
 * - shape: polygons filled with a colour or a cover, and a border.
 * - text: words on the map, standing at a place or on a thing: a point's name beside it, words on
 *   or along a line, a name across ground, a callout or a bubble.
 * - card: a panel with a title, words and a picture, standing on the map on a pin.
 * - media: a picture or a film laid over a box.
 * - particles: small marks drifting across the screen, such as snow, rain, ash, or sparks.
 * - entry: a piece on a board, such as something held, a post, a goal or a term.
 * - link: one entry on a board requiring another, or the two ruling each other out.
 * - category: a heading canvases are listed under.
 */
import { z } from "zod";
import { FilterSchema } from "./filters";
import { BoundsSchema, DisplaySchema } from "./map";
import { AssetVersionIdSchema, DescriptionSchema, IdSchema, NameSchema } from "./primitives";

/**
 * `[longitude, latitude]`, as GeoJSON has a position: longitude from -180 to 180, and latitude from
 * -85 to 85, as far as a flat map reaches. Earth's own on a globe, and the same axes on any other
 * canvas.
 */
export const MapPositionSchema = z.tuple([
  z.number().min(-180).max(180),
  z.number().min(-85).max(85),
]);
export type MapPosition = z.infer<typeof MapPositionSchema>;

/** `#rgb`, `#rrggbb`, or `#rrggbbaa`. */
export const MapColorSchema = z
  .string()
  .regex(
    /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,
    "A colour: #rgb, #rrggbb or #rrggbbaa.",
  );

/**
 * How a track goes from one key to the next: `step` holds the earlier value and changes at the
 * key's `at`, `linear` moves at an even pace, and `smooth` eases through the keys without stopping
 * at them, passing through each without overshooting it.
 */
export const EaseSchema = z.enum(["step", "linear", "smooth"]);
export type Ease = z.infer<typeof EaseSchema>;

export interface Key<Value> {
  at: number;
  value: Value;
}

/**
 * How a value changes, keyed on the clock its thing is written on: ticks on the timeline, the
 * scene's seconds in a scene. Before the first key and after the last, it holds.
 */
export interface Track<Value> {
  keys: Key<Value>[];
  ease?: Ease;
}

/** A value held as given, or keyed over time. */
export type Animated<Value> = Value | Track<Value>;

export const isTrack = <Value>(value: Animated<Value>): value is Track<Value> =>
  typeof value === "object" && value !== null && !Array.isArray(value) && "keys" in value;

export function animated<Value extends z.ZodType>(value: Value) {
  return z.union([
    value,
    z.strictObject({
      keys: z.array(z.strictObject({ at: z.number().nonnegative(), value })).min(1),
      ease: EaseSchema.optional(),
    }),
  ]);
}

const fraction = z.number().min(0).max(1);

/**
 * Numbers words show, by name, each held or keyed: keyed, it moves between keys by its ease. Words
 * given values are an ICU message, in English; words without them are only words. `{men, number}` shows a number with commas between thousands, `{men, number, integer}`
 * shows it rounded to whole, and `{men}` alone shows its digits as they are. `{guns, plural, one {#
 * gun} other {# guns}}` says words by a number, `#` standing for it, and `select`, `selectordinal`,
 * `=0` and `offset:` are as ICU has them. A number between keys is not whole, so a count that runs
 * up shows it whole: `Mons · {lost, number, integer} casualties`, `lost` keyed from 0 to 1638 over
 * five seconds, runs up through every number between, as fast as the keys and their ease say.
 */
export const ValuesSchema = z.record(
  z
    .string()
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      "A value's name is letters, digits and underscores, not starting with a digit.",
    ),
  animated(z.number()),
);
export type Values = z.infer<typeof ValuesSchema>;

/** A GeoJSON LineString's positions. */
const LinePositionsSchema = z.array(MapPositionSchema).min(2);
/**
 * A GeoJSON LineString, or a MultiLineString of parts. Parts that meet at longitude ±180 are drawn
 * as one line, so a line crossing the antimeridian is written as GeoJSON writes it, in two parts:
 * one ending at 180 and the next starting at -180, or the reverse.
 */
const LineStringsSchema = z.union([LinePositionsSchema, z.array(LinePositionsSchema).min(1)]);
const RingSchema = z.array(MapPositionSchema).min(3);
/** Polygons, each its outline and then any holes, or none. A ring need not repeat its first position. */
const PolygonsSchema = z.array(z.array(RingSchema).min(1));
const BoxSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]);

/**
 * Growing and shrinking with the map instead of keeping its size on the screen, as a campaign arrow
 * does: its pixels are then the map's own, as zoom counts them, given at `zoom`, twice as many a
 * zoom in and half as many a zoom out, so it covers the same ground at every zoom and on every
 * screen. On a screen of two device pixels to a pixel, 36 of them are 18 pixels, as the map itself
 * is drawn there at half the size. `min` and `max` hold it between that many pixels.
 */
export const ScaleSchema = z
  .strictObject({
    zoom: z.number(),
    min: z.number().nonnegative().optional(),
    max: z.number().positive().optional(),
  })
  .refine(({ min, max }) => min === undefined || max === undefined || min <= max, {
    message: "The smaller size comes first.",
  });
export type Scale = z.infer<typeof ScaleSchema>;

export const StrokeSchema = z.strictObject({
  color: animated(MapColorSchema),
  /** How wide it is, in pixels, 2 when left out. */
  width: z.number().nonnegative().optional(),
  /**
   * Dashes: `pattern` is dash and gap lengths in turn, in pixels, each dash ending square; `offset`
   * is how far they have moved along the line toward its last point, in pixels, held or keyed, so
   * keys run them, hold them still, or turn them back.
   */
  dash: z
    .strictObject({
      pattern: z.array(z.number().nonnegative()).min(1),
      offset: animated(z.number()).optional(),
    })
    .optional(),
  /**
   * A band round the line, `haloWidth` wider than it in all, 3 px when left out, its head
   * included. Under a line that hides what is beneath it; round a translucent line only, never
   * beneath it, so the line keeps its own colour, and in the line's own colour it is an outline. A
   * translucent dashed line's halo rings each dash.
   */
  halo: MapColorSchema.optional(),
  haloWidth: z.number().nonnegative().optional(),
  /**
   * A head at the end of what is drawn, its length 0.67 and its width 1.5 times 1.6 times the
   * line's width there, at least 8 px: whole from the first moment the line draws, leading it.
   * A translucent line and its head are drawn as one, so where they meet is no darker.
   */
  arrow: z.boolean().optional(),
  smooth: z.boolean().optional(),
  /**
   * Its width at its last point, for a line that widens or narrows along its length: `width` at
   * its first point and this at its last, changing evenly with the distance along it. Drawing it
   * in keeps the width each stretch has, so it grows no wider than the line is there.
   */
  endWidth: z.number().nonnegative().optional(),
  /**
   * Its width growing and shrinking with the map, as `ScaleSchema` says, `{ zoom, min, max }`: its
   * end width, halo, dashes and head keep their share of it.
   */
  scale: ScaleSchema.optional(),
  /**
   * Drawn as a hand draws it: wavering up to this many pixels to either side of its points,
   * gently, a few times along its length, as a pen draws over a map. A line keeps its own
   * wavering, the same every frame, and things on it stand on its points.
   */
  hand: z.number().positive().optional(),
  /**
   * How strongly it shows at chosen zooms, `[zoom, 0 to 1]` in order of zoom: even between them,
   * held past the first and last. `{ "zoom": [[2.4, 0], [7, 1]] }` is gone with the map far out,
   * fades in as it comes in, and is whole from zoom 7. Left out, whole at every zoom.
   */
  opacity: z.strictObject({ zoom: z.array(z.tuple([z.number(), fraction])).min(1) }).optional(),
});
export type Stroke = z.infer<typeof StrokeSchema>;

/**
 * A texture covering ground instead of one colour, painted from noise at the same grain on screen at
 * every zoom:
 *
 * - `mist`: grey-blue mist, paler where it is thick.
 * - `cloud`: white cloud tops lit from the northwest, softly creased.
 * - `haze`: pale haze in banks, hiding from a tenth to three quarters of what is beneath it.
 * - `parchment`: the blank ivory sheet of a chart not yet drawn, finely grained and ruled with a
 *   faint graticule every 1, 2, 5, 10 or 20 degrees, the finest at least 110 px apart. Ground it
 *   uncovers is inked in: the map comes through in blots, washed sepia until its colour comes.
 * - `antique`: the same chart grown old, its paper a warm cream gone brown toward the edge of what
 *   is drawn and freckled with age, its graticule in faded brown, inked in the same way.
 * - `void`: near black, cut along the middle of its edge, its shadow falling across the rest of the
 *   edge onto the ground beside it.
 *
 * Mist, cloud, haze and void drift on the wall clock, void slowest. Every cover but `haze` hides
 * what is drawn beneath it, and at least half opaque keeps presses and hovers from it. Its soft edge
 * lies outside the ground it covers, past its outline and into each hole and clearing, fraying
 * through its noise; along the rim of the world, at longitude ±180 or latitude ±85, it has none, so
 * cover meets cover there without a seam. Ground it comes to cover thickens in out of nothing and
 * ground it uncovers thins away, each place fading rather than appearing at once, over a second or
 * so of the order played; when the order jumps, it is where it then is at once. As its opacity falls
 * it thins the same way everywhere, and is gone at 0.
 */
export const CoverSchema = z.enum(["mist", "cloud", "parchment", "antique", "void", "haze"]);
export type Cover = z.infer<typeof CoverSchema>;

/**
 * A shape's cover, `{ "cover": "mist" }`. `feather` is how far its soft edge reaches from the ground
 * it covers, in kilometres: left out, a sixth of the side of a square as large as its largest ring,
 * and round a clearing a sixth of the side of a square as large as the clearing. `clears` opens a
 * hole round each point it names, wherever the point stands, reaching `km` from it, as sight would,
 * from the point's `from` to its `until` whether or not it shows.
 */
export const CoverFillSchema = z.strictObject({
  cover: CoverSchema,
  feather: z.number().positive().optional(),
  clears: z
    .array(z.strictObject({ point: IdSchema, km: animated(z.number().positive()) }))
    .min(1)
    .optional(),
});
export type CoverFill = z.infer<typeof CoverFillSchema>;

const common = {
  id: IdSchema,
  /**
   * What it is called. Players see it in two places only: in the atlas, when they pick a region or
   * a place, and on the map as the words of a text on it that gives none of its own. A name alone
   * draws nothing.
   */
  name: NameSchema.optional(),
  /**
   * What the atlas tells a player who picks a region or a place: for a region, its terrain, such as
   * Land or Coastal; for a place, a line or two about it. Nothing else shows it.
   */
  description: DescriptionSchema.optional(),
  /**
   * Facts about it, as a GeoJSON feature's properties: any names, each any JSON value, such as
   * `{ "side": "axis", "echelon": "corps" }`. Nothing draws them; filters read them.
   */
  properties: z.record(z.string(), z.unknown()).optional(),
  /**
   * When it appears: a tick on the timeline, or a second of the scene in a scene. Left out, from
   * where the write that places it starts, or as the scene begins. Until the first key of anything
   * it keys, it shows that key's value: a shape whose outline is keyed from tick 45 and has no
   * `from` shows its tick-45 outline from where its write starts.
   */
  from: z.number().nonnegative().optional(),
  /**
   * When it leaves, on the same clock as `from`. Left out, a thing on the timeline stays for good,
   * until a later change gives it an `until`; a thing a scene shows leaves when the scene ends.
   */
  until: z.number().nonnegative().optional(),
};

/**
 * How a thing comes onto the map as it appears (`enter`) or goes as it leaves (`exit`); left out,
 * it is there or gone at once. Each plays only while the map plays, over at most a second and a
 * half of the wall clock and none of the clock the thing is written on, so a scrubbed map shows it
 * there or not at once. Every thing on a map can `fade`; points and lines have more ways.
 */
const FadeSchema = z.enum(["fade"]);
const fades = { enter: FadeSchema.optional(), exit: FadeSchema.optional() };

/**
 * `fade`; `grow`, from nothing to its size; `muster`, forming as marks gather into it from round
 * about; or `build`, rising from its foot course by course.
 */
export const PointEntranceSchema = z.enum(["fade", "grow", "muster", "build"]);
/**
 * `fade`; `shrink` to nothing; `explode`, bursting into a fireball with its pieces flying; or
 * `dissolve` into motes that drift off.
 */
export const PointExitSchema = z.enum(["fade", "shrink", "explode", "dissolve"]);
/** `fade`, or `grow`, drawn from its start. */
export const LineEntranceSchema = z.enum(["fade", "grow"]);
/** `fade`, or `shrink`, taken back to its end. */
export const LineExitSchema = z.enum(["fade", "shrink"]);
/** Every way a thing comes onto the map. */
export type Entrance = z.infer<typeof PointEntranceSchema>;
/** Every way a thing goes. */
export type Exit = z.infer<typeof PointExitSchema>;

const onCanvas = {
  ...common,
  canvas: IdSchema,
  layer: z.number().optional(),
  /** How strongly it shows, 0 to 1, times how its `zoom` fades it. */
  opacity: animated(fraction).optional(),
  /**
   * The zooms it shows at, `[from, to]`: fading in over the `zoomFade` levels above `from` and out
   * over as many above `to`, so ranges that meet crossfade.
   *
   * Zoom 0 shows the whole canvas, and each level doubles the scale: the canvas is 512 × 2^zoom
   * device pixels across on every screen. A laptop's screen, about 2,000 device pixels across, shows
   * 1,400° ÷ 2^zoom of longitude; a phone's, about 1,200 across, a little over half that. On a
   * laptop, zoom 3 shows about 175°, half the world; 4 about 90°, a continent; 5 about 45°, Lisbon
   * to Moscow; 6 about 22°, a large country; 7 about 11°, a small one; 8 about 5.5°, a province; 10
   * about 1.4°, a city and the country around it; 12 about 0.35°, a city. A degree of longitude is
   * 111 km at the equator and 111 km × cos(latitude) away from it: 72 km at 50°.
   */
  zoom: z.tuple([z.number(), z.number()]).optional(),
  /** How many zoom levels it fades in and out over at the ends of its `zoom`: half when left out. */
  zoomFade: z.number().positive().max(4).optional(),
};

/** Raster tiles by their address, such as a basemap asset's, whose published tiles never move. */
export const RasterBasemapSchema = z.strictObject({
  type: z.literal("raster"),
  url: z.url(),
  maxZoom: z.number().int().nonnegative(),
  tileSize: z.number().int().positive(),
});

/** What lies under everything on a canvas: the standard world map, nothing, or raster tiles. */
export const CanvasBasemapSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("default") }),
  z.strictObject({ type: z.literal("empty") }),
  RasterBasemapSchema,
]);
export type CanvasBasemap = z.infer<typeof CanvasBasemapSchema>;

/**
 * Pixels at chosen zooms, `[zoom, px]` in order of zoom: `{ "zoom": [[4, 9], [8, 14]] }` is 9 px at
 * zoom 4 and farther out, 14 px at zoom 8 and closer in, and grows evenly between, 11.5 px at zoom
 * 6, as Mapbox's `["interpolate", ["linear"], ["zoom"], 4, 9, 8, 14]`.
 */
const ZoomCurveSchema = z.strictObject({
  zoom: z.array(z.tuple([z.number(), z.number().positive()])).min(1),
});

/** How tall letters are, in pixels: one size at every zoom, or sizes at chosen zooms. */
export const TextSizeSchema = z.union([z.number().positive(), ZoomCurveSchema]);
export type TextSize = z.infer<typeof TextSizeSchema>;

/** Where a text goes around its point: beside it, at a corner, or over it. */
export const PlacementSchema = z.enum([
  "right",
  "left",
  "top",
  "bottom",
  "top-right",
  "top-left",
  "bottom-right",
  "bottom-left",
  "center",
]);
export type Placement = z.infer<typeof PlacementSchema>;

/**
 * A colour for words, one of four things:
 *
 * - `#rgb`, `#rrggbb` or `#rrggbbaa`: a colour, the last two digits its opacity, alike on both
 *   themes.
 * - `{ light, dark }`: one colour for each theme. Players see the map in a light theme or a dark one,
 *   as they choose: `{ "light": "#1c1917", "dark": "#f5f5f4" }` is near black on the light map and
 *   near white on the dark one, as CSS's `light-dark()`.
 * - `marker`: the colour of what the text is on: a point's marker, its frame's fill or, without a
 *   frame, its glyph's colour; or a line's stroke. Standing at a place or across ground, a quiet
 *   grey, `{ "light": "#78716c", "dark": "#a8a29e" }`.
 * - `contrast`: near black or near white, whichever reads against the box behind the words, as
 *   CSS's `contrast-color()`.
 */
export const TextColorSchema = z.union([
  MapColorSchema,
  z.strictObject({ light: MapColorSchema, dark: MapColorSchema }),
  z.literal("marker"),
  z.literal("contrast"),
]);
export type TextColor = z.infer<typeof TextColorSchema>;

const pixels = z.number().nonnegative();

/**
 * How words look, each property named and meant as CSS's of the same name, or Mapbox's where CSS has
 * none. A canvas names its styles, and a text names one and overrides it property by property.
 */
export const TextStyleSchema = z.strictObject({
  /** The typeface: `serif`, the map's own and the default, or `sans-serif`. CSS `font-family`. */
  fontFamily: z.enum(["serif", "sans-serif"]).optional(),
  /**
   * How heavy the letters are, 100 thinnest to 900 heaviest: 400 regular, 500 medium, the default,
   * and 700 bold. CSS `font-weight`.
   */
  fontWeight: z.number().int().min(100).max(900).multipleOf(100).optional(),
  /** `italic` slants the letters; `normal` by default. CSS `font-style`. */
  fontStyle: z.enum(["normal", "italic"]).optional(),
  /** `small-caps` draws small letters as small capitals; `normal` by default. CSS `font-variant`. */
  fontVariant: z.enum(["normal", "small-caps"]).optional(),
  /**
   * How tall the letters are: beside a point, sized from its marker by default, and otherwise 11
   * px. CSS `font-size`.
   */
  fontSize: TextSizeSchema.optional(),
  /**
   * Sets the words in capitals or in small letters, whatever they are written in; `none` by
   * default. CSS `text-transform`.
   */
  textTransform: z.enum(["none", "uppercase", "lowercase"]).optional(),
  /**
   * Space added between letters, in ems, an em being the letters' size: 0.1 at 12 px adds 1.2 px.
   * 0 by default. CSS `letter-spacing`.
   */
  letterSpacing: z.number().min(0).max(0.5).optional(),
  /** The space from one line of wrapped words to the next, times `fontSize`; 1.3 by default. CSS `line-height`. */
  lineHeight: z.number().positive().optional(),
  /** The widest a line of words runs before it wraps, in pixels; by default words never wrap. CSS `max-width`. */
  maxWidth: z.number().positive().optional(),
  /** How wrapped lines line up with one another: `left`, `center`, the default, or `right`. CSS `text-align`. */
  textAlign: z.enum(["left", "center", "right"]).optional(),
  /** The letters' colour: near black on the light map and near white on the dark one by default. CSS `color`. */
  color: TextColorSchema.optional(),
  /** An outline round every letter, so words read over anything on the map: its colour, black by default. Mapbox `text-halo-color`. */
  haloColor: TextColorSchema.optional(),
  /**
   * How wide the outline is, in ems, an em being the letters' size, so it keeps to them as they
   * grow and shrink with the map: 0.06 at 20 px is 1.2 px. At most 0.14, and never drawn under two
   * of the screen's pixels; none by default. Mapbox `text-halo-width`, in ems.
   */
  haloWidth: z.number().nonnegative().max(0.14).optional(),
  /** What fills a box behind the words; no box by default. CSS `background-color`. */
  backgroundColor: TextColorSchema.optional(),
  /**
   * The room between the words and the box's edge, in pixels: one number all round, or `[across,
   * down]`; `[7, 3]` by default. CSS `padding`.
   */
  padding: z.union([pixels, z.tuple([pixels, pixels])]).optional(),
  /**
   * How round the box's corners are, in pixels, 9 by default; `full` rounds its ends into a pill.
   * CSS `border-radius`; `full` is ours.
   */
  borderRadius: z.union([pixels, z.literal("full")]).optional(),
  /** The colour of a line round the box's edge. CSS `border-color`. */
  borderColor: TextColorSchema.optional(),
  /** How wide that line is, in pixels; none by default. CSS `border-width`. */
  borderWidth: pixels.optional(),
  /** A pointer from the box's bottom edge to the place or point the text is on, as a speech bubble has. Ours. */
  tail: z.boolean().optional(),
  /** A small shape on the box: a `dot` before the words, or a `bar` down its left edge. Ours. */
  mark: z.enum(["dot", "bar"]).optional(),
  /** The mark's colour: `marker` by default. Ours. */
  markColor: TextColorSchema.optional(),
});
export type TextStyle = z.infer<typeof TextStyleSchema>;

const INK = { light: "#1c1917", dark: "#f5f5f4" } as const;
const MUTED = { light: "#57534e", dark: "#c4c0bc" } as const;
const GROUND = { light: "#ffffffeb", dark: "#171717e0" } as const;
const BOX = {
  backgroundColor: GROUND,
  borderColor: { light: "#0000001a", dark: "#ffffff1a" },
  borderWidth: 1,
  borderRadius: 5,
} as const;
const BUBBLE = {
  backgroundColor: GROUND,
  borderColor: { light: "#00000024", dark: "#ffffff2e" },
  borderWidth: 1,
  borderRadius: 9,
  padding: [9, 5] as [number, number],
  lineHeight: 1.33,
  textAlign: "left",
  tail: true,
} as const;

/**
 * The styles every canvas has, which its own of the same names replace:
 *
 * - `name`, the default beside a point: bold serif letters, white with a black halo, as the map
 *   names places.
 * - `along`, the default along a line: serif letters with a soft halo, as a river is named.
 * - `across`, the default across ground: bold serif capitals, white with a black halo, as the map
 *   names countries.
 * - `callout`, the default flat on a line and standing at a place: sans-serif letters on a box
 *   with a bar in the marker's colour.
 * - `pill`: sans-serif letters on a pill-shaped box with a dot in the marker's colour.
 * - `region`: small bold spaced sans-serif capitals in a quieter ink on a box, for ground named at
 *   a point.
 * - `water`: italic blue sans-serif letters on a box, as water is named at a point.
 * - `tag`: small bold sans-serif capitals on a box in the marker's colour, for a unit or a badge.
 * - `banner`: large bold sans-serif letters on a box in the marker's colour, for a headline.
 * - `note`: small sans-serif letters in a quieter ink on a box, for an aside or a date.
 * - `speech`: sans-serif letters in a speech bubble, for words said.
 * - `thought`: italic violet sans-serif letters in a speech bubble, for words thought.
 * - `narration`: italic sans-serif letters in a speech bubble, for a narrator's words.
 */
export const BUILT_IN_STYLES = {
  name: {
    fontFamily: "serif",
    fontWeight: 700,
    color: "#ffffff",
    haloColor: "#000000",
    haloWidth: 0.054,
  },
  along: {
    fontFamily: "serif",
    fontWeight: 600,
    fontSize: 11,
    color: INK,
    haloColor: { light: "#ffffffe6", dark: "#000000bf" },
    haloWidth: 0.136,
  },
  across: {
    fontFamily: "serif",
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#ffffff",
    haloColor: "#000000",
    haloWidth: 0.062,
  },
  callout: {
    fontFamily: "sans-serif",
    fontWeight: 600,
    fontSize: 10.5,
    lineHeight: 1.33,
    maxWidth: 220,
    textAlign: "left",
    color: INK,
    ...BOX,
    mark: "bar",
    markColor: "marker",
  },
  pill: {
    fontFamily: "sans-serif",
    fontWeight: 600,
    fontSize: 10.5,
    lineHeight: 1.33,
    maxWidth: 200,
    textAlign: "left",
    color: INK,
    ...BOX,
    borderRadius: "full",
    mark: "dot",
    markColor: "marker",
  },
  region: {
    fontFamily: "sans-serif",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.12,
    fontSize: 9,
    color: MUTED,
    ...BOX,
  },
  water: {
    fontFamily: "sans-serif",
    fontWeight: 600,
    fontStyle: "italic",
    fontSize: 10.5,
    color: { light: "#1d4ed8", dark: "#93c5fd" },
    ...BOX,
  },
  tag: {
    fontFamily: "sans-serif",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.06,
    fontSize: 9,
    color: "contrast",
    backgroundColor: "marker",
    borderRadius: 4,
    padding: [5, 2],
  },
  banner: {
    fontFamily: "sans-serif",
    fontWeight: 700,
    fontSize: 13,
    color: "contrast",
    backgroundColor: "marker",
    borderRadius: 4,
    padding: [8, 4],
  },
  note: { fontFamily: "sans-serif", fontWeight: 400, fontSize: 10, color: MUTED, ...BOX },
  speech: {
    fontFamily: "sans-serif",
    fontWeight: 500,
    fontSize: 12,
    maxWidth: 320,
    color: INK,
    ...BUBBLE,
  },
  thought: {
    fontFamily: "sans-serif",
    fontWeight: 500,
    fontStyle: "italic",
    fontSize: 12,
    maxWidth: 320,
    color: { light: "#8b5cf6", dark: "#c4b5fd" },
    ...BUBBLE,
  },
  narration: {
    fontFamily: "sans-serif",
    fontWeight: 400,
    fontStyle: "italic",
    fontSize: 12,
    maxWidth: 400,
    color: INK,
    ...BUBBLE,
  },
} as const satisfies Record<string, TextStyle>;

/**
 * How a board lays its entries out: `list`, in order; `grid`, each in its cell, `columns` across,
 * the rows as many as the entries need; `tree`, each under the entry it names, from its roots.
 */
export const ArrangementSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("list") }),
  z.strictObject({ type: z.literal("grid"), columns: z.number().int().min(1).max(24) }),
  z.strictObject({ type: z.literal("tree") }),
]);
export type Arrangement = z.infer<typeof ArrangementSchema>;

/** A picture by name: a Game Icons icon, as a point's glyph names one, or an asset. */
export const PictureSchema = z.union([
  z.strictObject({ icon: z.string().min(1) }),
  z.strictObject({ asset: AssetVersionIdSchema }),
]);
export type Picture = z.infer<typeof PictureSchema>;

const CanvasShape = z.strictObject({
  kind: z.literal("canvas"),
  ...common,
  /** The category it is listed under, by id. */
  category: IdSchema.optional(),
  basemap: z.strictObject({ light: CanvasBasemapSchema, dark: CanvasBasemapSchema }).optional(),
  display: DisplaySchema.optional(),
  startingView: BoundsSchema.optional(),
  /** Its text styles, by the names texts on it give them. */
  styles: z.record(IdSchema, TextStyleSchema).optional(),
  arrangement: ArrangementSchema.optional(),
  /**
   * Whether it is a world of its own, as the Earth or the Moon is, which takes all of the screen
   * when the camera goes to it; any other canvas, a board or a map of somewhere such as a city,
   * comes up in a window over the world, which still shows round it. Only a map can be whole.
   */
  whole: z.boolean().optional(),
});

/**
 * A surface things are placed on. A map, drawn by the map: a world, a galaxy, a city, a room, with
 * its `basemap` and `display`. Or a board, laid out by the app in its own UI: entries and the links
 * between them, as its `arrangement` says, such as an inventory, a chain of command or a list of
 * terms. Every canvas is drawn at a phone's width as well as a desktop's, so a board's arrangement
 * reads in a narrow column.
 */
export const CanvasThingSchema = CanvasShape.refine(
  canvas =>
    canvas.arrangement === undefined
      ? canvas.basemap !== undefined && canvas.display !== undefined
      : canvas.basemap === undefined &&
        canvas.display === undefined &&
        canvas.startingView === undefined &&
        canvas.styles === undefined &&
        canvas.whole === undefined,
  "A canvas is a map, with a basemap and a display, or a board, with an arrangement and nothing a map has.",
);

export const SignSchema = z.enum([
  "x",
  "slash",
  "box",
  "dot",
  "bar",
  "plus",
  "ring",
  "chevron",
  "oval",
]);
export type Sign = z.infer<typeof SignSchema>;

export const PointSymbolSchema = z.strictObject({
  frame: z
    .strictObject({
      shape: z.enum(["rect", "circle", "diamond", "hexagon", "triangle", "star"]),
      fill: animated(MapColorSchema),
      stroke: MapColorSchema.optional(),
      aspect: z.number().positive().optional(),
    })
    .optional(),
  glyph: z
    .union([
      z.strictObject({ icon: z.string().min(1) }),
      z.strictObject({ sign: SignSchema }),
      z.strictObject({ text: animated(z.string()) }),
      z.strictObject({ asset: AssetVersionIdSchema, fit: z.enum(["cover", "contain"]).optional() }),
    ])
    .optional(),
  color: animated(MapColorSchema).optional(),
  halo: MapColorSchema.optional(),
});
export type PointSymbol = z.infer<typeof PointSymbolSchema>;

/**
 * How big a point is drawn: pixels, held or keyed over time, or pixels at chosen zooms, so a
 * marker can be small and spare zoomed out and grow as the map comes in. A point's `scale` holds
 * its size in pixels to the ground instead.
 */
export const SizeSchema = z.union([animated(z.number().positive()), ZoomCurveSchema]);
export type Size = z.infer<typeof SizeSchema>;

/**
 * A point's position: held, or keyed over time. Keyed, its keys are its path: it goes from each key
 * to the next in a straight line in its coordinates, as a GeoJSON line runs, and a way that bends
 * takes a key at every bend. To cross the antimeridian it passes through it: a key at longitude 180
 * and the next at -180, or the reverse, at the same moment. Its ease is only how fast it goes along
 * that path: `linear`, the default, at an even pace from each key to the next;
 * `smooth`, changing pace gently through the keys without stopping at them, slowing to a stop where
 * two keys hold it in one place and setting off gently from there; `step`, staying at each key
 * until the next one's `at`.
 */
export const PointPositionSchema = z.union([
  MapPositionSchema,
  z.strictObject({
    keys: z
      .array(
        z.strictObject({
          at: z.number().nonnegative(),
          value: MapPositionSchema,
        }),
      )
      .min(1),
    ease: EaseSchema.optional(),
  }),
]);
export type PointPosition = z.infer<typeof PointPositionSchema>;

export const PointThingSchema = z.strictObject({
  kind: z.literal("point"),
  ...onCanvas,
  enter: PointEntranceSchema.optional(),
  exit: PointExitSchema.optional(),
  position: PointPositionSchema,
  symbol: PointSymbolSchema,
  size: SizeSchema.optional(),
  /** Its size in the map's own pixels, growing and shrinking with the map, as `ScaleSchema` says. */
  scale: ScaleSchema.optional(),
  /**
   * Its marker turned clockwise, in degrees, held or keyed; or `travel`, its top facing the way it
   * goes along the leg of its path it is on, or last went along while it stands still; or
   * `travel-flip`, its marker mirrored while that leg runs west.
   */
  rotation: z
    .union([animated(z.number()), z.literal("travel"), z.literal("travel-flip")])
    .optional(),
  glow: z
    .strictObject({ color: animated(MapColorSchema), radius: z.number().nonnegative() })
    .optional(),
  /**
   * Something happening there now, beating out from it: in the colour given, or its marker's, a
   * beat every `every` seconds, 1.6 when left out, the first as it appears, `count` beats or as
   * many as it shows for. Each beat is drawn in its `look`:
   *
   * - `rings`, the default: two rings spreading from the marker's edge until they are three times
   *   as wide as it, half a beat apart, growing bolder as they spread and fading as they go.
   * - `ring`: one fine ring, 1.5 px, spreading from the marker's edge until it is four times as
   *   wide as the marker, quickly at first, fading as it goes.
   * - `echo`: that ring and a second a tenth of a beat behind it, fainter and finer.
   * - `flash`: a soft glow over the marker, there at once and fading over the beat.
   * - `splash`: a soft disc spreading from the marker until it is four times as wide, fading as
   *   it goes, with no edge.
   * - `pop`: the marker itself swelling by a quarter and settling back over the first third of
   *   the beat, with a glow while it does.
   *
   * Beats keep to the clock on the wall, played or paused, from when it appears.
   */
  pulse: z
    .strictObject({
      look: z.enum(["rings", "ring", "echo", "flash", "splash", "pop"]).optional(),
      color: MapColorSchema.optional(),
      every: z.number().positive().optional(),
      count: z.number().int().positive().optional(),
    })
    .optional(),
});

/**
 * A line along the edges of shapes rather than points of its own: the outline of the shapes on its
 * canvas `of` holds for, as each stands at every moment, so it meets their ground exactly and moves
 * as they spread. Where two of those shapes touch, the edge they share is no part of it. With
 * `against`, only where the ground a kilometre beyond the outline lies under a shape `against` holds
 * for. It runs with the shapes it follows on its left, in stretches wherever it breaks, the longest
 * first, and `drawn` takes a share of all of them.
 */
export const EdgeSchema = z.strictObject({
  edge: z.strictObject({ of: FilterSchema, against: FilterSchema.optional() }),
});
export type Edge = z.infer<typeof EdgeSchema>;

/**
 * A line round a point, by id, that goes wherever the point goes: each position kilometres east
 * and north of the point, so `[0, 0]` is the point itself and `[12, -5]` lies 12 km east and 5 km
 * south of it, on the flat of the Earth round the point, keeping its form and size on the ground.
 */
export const AroundSchema = z.strictObject({
  around: z.strictObject({
    point: IdSchema,
    km: animated(z.array(z.tuple([z.number(), z.number()])).min(2)),
  }),
});
export type Around = z.infer<typeof AroundSchema>;

export const LineThingSchema = z.strictObject({
  kind: z.literal("line"),
  ...onCanvas,
  enter: LineEntranceSchema.optional(),
  exit: LineExitSchema.optional(),
  /**
   * Positions on the map, held or keyed, as a LineString or a MultiLineString; the edges of shapes
   * (`Edge`); or kilometres round a point (`Around`). Its parts are one line: `drawn` measures along
   * them in order, and an arrow's head goes at the end of the last. Between two keys the points of
   * both, every point of each kept, are paired in order along both lines at the least distance
   * between partners, and each moves straight to its partner: what both keys share holds still, a
   * stretch only one has grows out of, or shrinks into, the nearest point of the other, and the line
   * never folds back on itself.
   */
  geometry: z.union([animated(LineStringsSchema), EdgeSchema, AroundSchema]),
  /**
   * Every stretch between two of its points drawn as a circular arc instead of straight, as TikZ's
   * `bend left` and `bend right` draw: leaving each point `degrees` off the straight way, to the
   * `side` given as you travel from its first point to its last, and arriving at the same angle
   * from the other side. 15 is gentle, 30 a clear arc, 60 strong, 90 half a circle; past 90 the
   * arc swells wider than the stretch. The same bend looks the same at any length and zoom. With
   * `smooth`, the corners where arcs meet are rounded too.
   */
  bend: z
    .strictObject({ side: z.enum(["left", "right"]), degrees: z.number().gt(0).lt(180) })
    .optional(),
  /** The stretch of the line drawn, as fractions of its length from its start: [0, 1] is all of it. */
  drawn: animated(z.tuple([fraction, fraction])).optional(),
  stroke: StrokeSchema,
});

/**
 * How a shape's geometry becomes a key's from the key before it. `spread`, the only way so far:
 * what both keys cover stays, with the edges they share; what only the later key covers is covered
 * nearest the earlier key's ground first, so the edge moves out parallel to where it was; what only
 * the earlier key covers is given up farthest from the later key's ground first, so the edge pulls
 * back toward it. A small earlier key, even a few metres across, is where new ground spreads from.
 * A key with an `origin` measures both from its origin instead. Ground goes only where a key leaves
 * it out, and the last key stays: a polygon shrunk to a few metres stays on the map that size.
 */
export const ShapeMorphSchema = z.enum(["spread"]);
export type ShapeMorph = z.infer<typeof ShapeMorphSchema>;

/**
 * A shape's polygons, held as given, or keyed over time, each key saying how it is reached. A key
 * may hold no polygons, for ground not there yet or gone.
 */
export const ShapeGeometrySchema = z.union([
  PolygonsSchema,
  z.strictObject({
    keys: z
      .array(
        z.strictObject({
          at: z.number().nonnegative(),
          value: PolygonsSchema,
          /** How the key before becomes this one: `spread` when left out. */
          morph: ShapeMorphSchema.optional(),
          /**
           * Where the change into this key starts: points and lines on the map, each line its
           * positions in order and a point a line of one. Ground this key gains is covered nearest
           * them first, and ground it gives up goes farthest from them first, so the change spreads
           * out from them, or draws back into them, rather than from the ground before. A key needs
           * one when it takes the shape to nothing or brings it from nothing. Otherwise, without
           * one, ground it takes is reached from the ground before, a piece apart from the rest
           * from its side nearest it, and ground it gives up draws back toward the ground it keeps.
           */
          origin: z.array(z.array(MapPositionSchema).min(1)).min(1).optional(),
        }),
      )
      .min(1),
    ease: EaseSchema.optional(),
  }),
]);
export type ShapeGeometry = z.infer<typeof ShapeGeometrySchema>;

export const ShapeThingSchema = z.strictObject({
  kind: z.literal("shape"),
  ...onCanvas,
  ...fades,
  geometry: ShapeGeometrySchema,
  /** A colour, or a cover. */
  fill: z.union([animated(MapColorSchema), CoverFillSchema]).optional(),
  border: StrokeSchema.optional(),
});

const StretchSchema = z
  .tuple([fraction, fraction])
  .refine(([from, to]) => from < to, "A stretch starts before it ends.");

/**
 * Where a text is, and how it sits there:
 *
 * - `at`: standing at a place, held or keyed to go from key to key as a point's position does.
 * - `point`: beside a point, following it, at the first `placement` with room; left out, right,
 *   left, top, bottom.
 * - `line`: set flat on a line, over the `middle` of its stretch drawn, the default, just before
 *   its `start`, or just past its `end`, beyond an arrow's head.
 * - `along`: letters along a line, by id, or along a `path`, following its general way rather than
 *   each turn, on its `left` or `right` as you travel it or just above it as it reads, over the
 *   stretch `over` gives as fractions of its length.
 * - `across`: spaced capitals across ground, the shapes on its canvas a filter holds for, the name
 *   once on each separate piece along its longest way; or along a `path`, spanning it end to end.
 */
export const TextOnSchema = z.union([
  z.strictObject({ at: animated(MapPositionSchema) }),
  z.strictObject({
    point: IdSchema,
    placement: z.union([PlacementSchema, z.array(PlacementSchema).min(1)]).optional(),
  }),
  z.strictObject({ line: IdSchema, spot: z.enum(["middle", "start", "end"]).optional() }),
  z.strictObject({
    along: z.union([IdSchema, z.strictObject({ path: animated(LineStringsSchema) })]),
    side: z.enum(["left", "right"]).optional(),
    over: animated(StretchSchema).optional(),
  }),
  z.strictObject({
    across: z.union([FilterSchema, z.strictObject({ path: animated(LineStringsSchema) })]),
  }),
]);
export type TextOn = z.infer<typeof TextOnSchema>;

/**
 * Words on the map. A text shows by its own `from`, `until`, `zoom` and `opacity`, and one on a
 * point or line only while that thing is on the map. Its style is its canvas's or a built-in one:
 * `name` beside a point, `along` along a line, `across` across ground, and `callout` flat on a line
 * or standing at a place by default. Any of a style's properties it gives replace the style's.
 */
export const TextThingSchema = z.strictObject({
  kind: z.literal("text"),
  ...onCanvas,
  ...fades,
  on: TextOnSchema,
  /** Its words. Left out on a point or line, that thing's `name`. */
  text: animated(z.string()).optional(),
  /** Numbers its words show, by name: with them, its words are an ICU message. */
  values: ValuesSchema.optional(),
  /** Its style, by name: its canvas's own, or one every canvas has. */
  style: IdSchema.optional(),
  ...TextStyleSchema.shape,
  /** Where it sits from where it is, in pixels right and down. */
  offset: z.tuple([z.number(), z.number()]).optional(),
  /**
   * Where texts want the same room, higher priorities take it first, and one that finds none is
   * left out until there is room. Among equal priorities, those already showing go first, then
   * those beside larger markers, then whichever comes first on the map. Left out, it is always
   * drawn, and the others give way to it.
   */
  priority: z.number().optional(),
  /**
   * Its words coming in as they are said. `shown` is how much of them shows, from 0 to 1, keyed to
   * pace them; `by` is what comes in at once, each `letter`, the default, or each `word`; `lead` is
   * what shows after the last of them while more are to come: a `caret` or `none`, the default.
   * The text is laid out whole from the start, so nothing moves as it comes in.
   */
  reveal: z
    .strictObject({
      shown: animated(fraction),
      by: z.enum(["letter", "word"]).optional(),
      lead: z.enum(["caret", "none"]).optional(),
    })
    .optional(),
});

/**
 * A card standing on the map: its `title`, its words under it in Markdown, and its `image` over
 * them, as wide as a panel, on a pin at its place or its point. As it appears it drops onto its
 * place a little large and turned and lands within two thirds of a second; as it leaves it lifts
 * away off the top of the screen over the half second before its `until`.
 */
export const CardThingSchema = z.strictObject({
  kind: z.literal("card"),
  ...onCanvas,
  on: z.union([
    z.strictObject({ at: animated(MapPositionSchema) }),
    z.strictObject({ point: IdSchema }),
  ]),
  title: z.string().trim().min(1).max(200),
  text: animated(z.string().max(20_000)).optional(),
  image: AssetVersionIdSchema.optional(),
});

/** A picture or a film from the asset catalog, laid on its canvas. */
export const MediaThingSchema = z.strictObject({
  kind: z.literal("media"),
  ...onCanvas,
  ...fades,
  asset: AssetVersionIdSchema,
  /** The box it is laid over: `[west, south, east, north]` on its canvas, held or keyed. */
  box: animated(BoxSchema),
  /** `contain`, the default, shows all of it inside the box; `cover` fills the box, cropped. */
  fit: z.enum(["contain", "cover"]).optional(),
  /**
   * How a film plays. From when it appears it plays at its own pace in real time, from `start`
   * seconds into it, and pauses while the map does; at its `until` it is gone wherever it has got
   * to. `speed`, keyed on its thing's clock, is how fast it plays at each moment: 1 its own pace,
   * 0 paused, 2 twice as fast. A scrubbed map shows the frame at `start`.
   */
  video: z
    .strictObject({
      start: z.number().nonnegative().optional(),
      speed: animated(z.number().nonnegative()).optional(),
      loop: z.boolean().optional(),
      sound: z.boolean().optional(),
    })
    .optional(),
});

/**
 * Many small marks drifting across the screen, not the map, such as snow, rain, ash, or sparks,
 * wrapping round its edges. They drift on the wall clock, played or paused.
 */
export const ParticlesThingSchema = z.strictObject({
  kind: z.literal("particles"),
  ...onCanvas,
  ...fades,
  /**
   * Marks per 100 × 100 pixels of screen where they are thickest, at most 6,000 on a screen:
   * 0.5 is about 65 on a laptop's, a drizzle, and 5 about 650, steady snow.
   */
  density: animated(z.number().nonnegative()),
  /** Pixels a second, right and down; each mark goes at between 0.6 and 1.4 times it. */
  drift: z.tuple([z.number(), z.number()]),
  /** How far each mark swings to either side as it drifts, in pixels. */
  sway: z.number().nonnegative().optional(),
  /** The smallest and largest mark, in pixels. */
  size: z
    .tuple([z.number().positive(), z.number().positive()])
    .refine(([least, most]) => least <= most, "The smaller size comes first."),
  color: animated(MapColorSchema),
  /**
   * `dot`, the default, `square`, and `streak`, a short line along the drift. `spark` is a dot
   * that adds its light to what is beneath. `ember` is a spark with a soft glow that flickers on
   * its own beat, for fire. `puff` is a soft round cloud three times its size, its edge fading out,
   * at half strength and slowly thickening and thinning, for gas, smoke and steam.
   */
  shape: z.enum(["dot", "streak", "square", "spark", "ember", "puff"]).optional(),
  seed: z.string().optional(),
});

const onBoard = { ...common, canvas: IdSchema };

/**
 * A piece on a board: something held, a post, a goal, a term, a date. Its `place` is its spot: in
 * a list, its place in the order, from 0; on a grid, its cell, `[column, row]` from 0; in a tree,
 * the entry it sits under, or its place among the roots for one that sits under none.
 */
export const EntryThingSchema = z.strictObject({
  kind: z.literal("entry"),
  ...onBoard,
  place: animated(
    z.union([
      z.number().int().nonnegative(),
      z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]),
      IdSchema,
    ]),
  ),
  title: animated(NameSchema),
  picture: PictureSchema.optional(),
  /** What it is, in Markdown, shown when it is pressed. */
  words: animated(z.string().max(20_000)).optional(),
  /** What stands on its right, any words: "7 coins", "40,000", "12 Feb 1912", "Agreed". */
  value: animated(z.string().trim().min(1).max(60)).optional(),
  /** `open`, to be had or done; `underway`; `done`; `locked`, not yet within reach; or `lost`. */
  state: animated(z.enum(["open", "underway", "done", "locked", "lost"])).optional(),
  /** How far one underway has gone, 0 to 1. */
  progress: animated(fraction).optional(),
});

/** One entry requiring another, or the two ruling each other out, drawn between them on their board. */
export const LinkThingSchema = z.strictObject({
  kind: z.literal("link"),
  ...onBoard,
  /** The two entries: the first requires the second, or excludes it. */
  ends: z.tuple([IdSchema, IdSchema]),
  type: z.enum(["requires", "excludes"]),
});

/** A heading a perspective's canvases are listed under. */
export const CategoryThingSchema = z.strictObject({
  kind: z.literal("category"),
  ...common,
  name: NameSchema,
  emblem: PictureSchema.optional(),
});

export const ThingSchema = z.discriminatedUnion("kind", [
  CanvasThingSchema,
  PointThingSchema,
  LineThingSchema,
  ShapeThingSchema,
  TextThingSchema,
  CardThingSchema,
  MediaThingSchema,
  ParticlesThingSchema,
  EntryThingSchema,
  LinkThingSchema,
  CategoryThingSchema,
]);

export type CanvasThing = z.infer<typeof CanvasThingSchema>;
/** A canvas the map draws. */
export type MapCanvas = CanvasThing & Required<Pick<CanvasThing, "basemap" | "display">>;
/** A canvas the app lays out. */
export type BoardCanvas = CanvasThing & { arrangement: Arrangement };
export const isMapCanvas = (canvas: CanvasThing): canvas is MapCanvas =>
  canvas.arrangement === undefined;
export const isBoardCanvas = (canvas: CanvasThing): canvas is BoardCanvas =>
  canvas.arrangement !== undefined;
export type EntryThing = z.infer<typeof EntryThingSchema>;
export type LinkThing = z.infer<typeof LinkThingSchema>;
export type CategoryThing = z.infer<typeof CategoryThingSchema>;
export type PointThing = z.infer<typeof PointThingSchema>;
export type LineThing = z.infer<typeof LineThingSchema>;
/** Whether a line runs along the edges of shapes rather than points of its own. */
export const isEdge = (geometry: LineThing["geometry"]): geometry is Edge =>
  !Array.isArray(geometry) && "edge" in geometry;
/** Whether a line is given round a point rather than on the map. */
export const isAround = (geometry: LineThing["geometry"]): geometry is Around =>
  !Array.isArray(geometry) && "around" in geometry;
export type ShapeThing = z.infer<typeof ShapeThingSchema>;
/** Whether a shape's fill is a cover rather than a colour. */
export const isCover = (fill: ShapeThing["fill"]): fill is CoverFill =>
  typeof fill === "object" && "cover" in fill;
export type TextThing = z.infer<typeof TextThingSchema>;
export type CardThing = z.infer<typeof CardThingSchema>;
export type MediaThing = z.infer<typeof MediaThingSchema>;
export type ParticlesThing = z.infer<typeof ParticlesThingSchema>;
export type Thing = z.infer<typeof ThingSchema>;
export type ThingKind = Thing["kind"];
/** A thing on a board. */
export type BoardThing = EntryThing | LinkThing;
/** A thing the map draws on one of its canvases. */
export type DrawnThing = Exclude<Thing, CanvasThing | CategoryThing | BoardThing>;
const UNDRAWN = new Set<ThingKind>(["canvas", "category", "entry", "link"]);
export const isDrawnThing = (thing: Thing): thing is DrawnThing => !UNDRAWN.has(thing.kind);

type ChangeOf<T> = T extends Thing
  ? { id: string; at?: number } & Partial<Omit<T, "id" | "kind" | "from" | "enter">>
  : never;
/**
 * Any of a thing's properties but its id and kind, and when and how it appears, from `at`. Each
 * replaces what the thing had.
 */
export type Change = ChangeOf<Thing>;

const changing = { id: IdSchema, at: z.number().nonnegative().optional() };

/**
 * What a change to a thing of each kind may give: any of its properties but its kind, and when and
 * how it appears, since a thing appears once.
 */
export const CHANGE_SCHEMAS: Record<ThingKind, z.ZodType> = {
  canvas: CanvasShape.partial().omit({ kind: true, from: true }).extend(changing),
  point: PointThingSchema.partial().omit({ kind: true, from: true, enter: true }).extend(changing),
  line: LineThingSchema.partial().omit({ kind: true, from: true, enter: true }).extend(changing),
  shape: ShapeThingSchema.partial().omit({ kind: true, from: true, enter: true }).extend(changing),
  text: TextThingSchema.partial().omit({ kind: true, from: true, enter: true }).extend(changing),
  card: CardThingSchema.partial().omit({ kind: true, from: true }).extend(changing),
  media: MediaThingSchema.partial().omit({ kind: true, from: true, enter: true }).extend(changing),
  particles: ParticlesThingSchema.partial()
    .omit({ kind: true, from: true, enter: true })
    .extend(changing),
  entry: EntryThingSchema.partial().omit({ kind: true, from: true }).extend(changing),
  link: LinkThingSchema.partial().omit({ kind: true, from: true }).extend(changing),
  category: CategoryThingSchema.partial().omit({ kind: true, from: true }).extend(changing),
};

/**
 * How the camera moves to a key from the key before it, on one canvas:
 *
 * - `step` holds the earlier key and cuts at this key's `at`.
 * - `linear` moves at an even pace.
 * - `pan` eases out of the earlier key and into this one, and stops at it.
 * - `fly` goes out and comes back in, only as far out as seeing from one key to the next on the
 *   player's screen needs, covering the distance fastest while it is out.
 * - `smooth` glides through the keys without stopping at them; between far-apart keys close
 *   together in time it whips past.
 */
export const CameraMoveSchema = z.enum(["step", "linear", "pan", "fly", "smooth"]);
export type CameraMove = z.infer<typeof CameraMoveSchema>;

/**
 * How a shot comes in from the shot before it: `cut`, the default; `crossfade`, fading in over it;
 * or `slide`, sliding in from the side as it slides out. A crossfade or slide takes the seconds
 * between the last moment of the shot before and this shot's first.
 */
export const CameraSwitchSchema = z.enum(["cut", "crossfade", "slide"]);
export type CameraSwitch = z.infer<typeof CameraSwitchSchema>;

/**
 * A shot on a map: keys of the ground it frames, in the scene's seconds, each an extent every
 * screen fits whole, wide or tall. A key is where the camera arrives: a move takes all the seconds
 * between two keys, so it rests only where two keys in a row frame the same. On a canvas that
 * repeats it goes the shorter way round; a key on the way sends it the long way.
 */
export const MapShotSchema = z.strictObject({
  map: IdSchema,
  in: CameraSwitchSchema.optional(),
  keys: z
    .array(
      z.strictObject({
        at: z.number().nonnegative(),
        extent: BoundsSchema,
        ease: CameraMoveSchema.optional(),
      }),
    )
    .min(1),
  /** How the camera moves to each key that gives no `ease` of its own: `pan` when left out. */
  ease: CameraMoveSchema.optional(),
});
export type MapShot = z.infer<typeof MapShotSchema>;

/** A shot of a board, all of it, from `at`. */
export const BoardShotSchema = z.strictObject({
  board: IdSchema,
  in: CameraSwitchSchema.optional(),
  at: z.number().nonnegative(),
});
export type BoardShot = z.infer<typeof BoardShotSchema>;

/**
 * Where the camera looks while a scene plays: its shots in order, each on one canvas from its first
 * moment until the next shot's. It starts at the first, the map panning there from wherever it
 * stood, and holds after the last.
 *
 * What is on screen is one world, a `whole` canvas, with windows over it for any other canvas, the
 * last opened in front. A shot on a whole canvas takes the screen under the windows up; one on a
 * canvas whose window is up brings it to the front, closing those in front of it; one on any other
 * opens its window in front. A desktop has room for three windows and a phone for one, so a window
 * opening past that closes the first opened.
 */
export const CameraSchema = z.strictObject({
  shots: z.array(z.union([MapShotSchema, BoardShotSchema])).min(1),
});
export type Camera = z.infer<typeof CameraSchema>;
export type Shot = Camera["shots"][number];
